import { Button, Grid, Box, IconButton, Stack, Typography, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const IndoorBillingManagementReport = ({ onClose, onAdd, reportId }) => {
  const { pathname } = useLocation();
  
  console.log('Current pathname:', pathname);
  console.log('Report ID:', reportId);
  
  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        type: Yup.string().required('Please select a report type'),
        doctor: Yup.string().required('Please select Doctor'),
        service: Yup.string().required('Please select Service')
      })
    ),
    mode: 'all',
    defaultValues: {
      type: '',
      doctor: '',
      service: ''
    }
  });

  const { handleSubmit, setValue, watch } = methods;
  const typeWatch = watch('type');

  useEffect(() => {
    if (!typeWatch || !reportId) return;
    
    console.log('Type selected:', typeWatch);
    console.log('Report ID for print:', reportId);
    
    const routeMap = {
      'detail': '/detail',
      'detail1': '/detail1',
      'summary': '/summary',
      'summary1': '/summary1',
      'summary2': '/summary2',
      'group': '/group'
    };

    const route = routeMap[typeWatch];
    if (route) {
      const printUrl = `/print${pathname}${route}?refId=${reportId}`;
      console.log('Opening URL:', printUrl);
      
      // Try different approaches to open the window
      const printWindow = window.open(printUrl, '_blank');
      
      if (printWindow) {
        console.log('Window opened successfully');
        // Add focus to the new window
        printWindow.focus();
      } else {
        console.log('Window was blocked by popup blocker');
        // Fallback: Show alert or alternative action
        alert('Popup was blocked. Please allow popups for this site.');
      }
      
      // Reset the selection after opening the print window
      setTimeout(() => {
        setValue('type', '');
      }, 100);
    }
  }, [typeWatch, pathname, reportId, setValue]);

  const formSubmitHandler = (formValues) => {
    console.log('Form submitted:', formValues);
    if (onAdd) {
      onAdd(formValues);
    }
  };

  // Alternative approach: Manual print button
  const handleManualPrint = () => {
    if (!typeWatch || !reportId) {
      alert('Please select a report type');
      return;
    }
    
    const routeMap = {
      'detail': '/detail',
      'detail1': '/detail1',
      'summary': '/summary',
      'summary1': '/summary1',
      'summary2': '/summary2',
      'group': '/group'
    };

    const route = routeMap[typeWatch];
    if (route) {
      const printUrl = `/print${pathname}${route}?refId=${reportId}`;
      console.log('Manual print URL:', printUrl);
      
      const printWindow = window.open(printUrl, '_blank');
      if (printWindow) {
        printWindow.focus();
      } else {
        alert('Popup was blocked. Please allow popups for this site.');
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        spacing={3}
        sx={{
          height: '100%',
          padding: 3,
          backgroundColor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '700px',
          margin: 'auto'
        }}
        component="form"
        onSubmit={handleSubmit(formSubmitHandler)}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" paddingY={2} paddingX={3}>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#333' }}>
            Print Report
          </Typography>
          <IconButton onClick={onClose} color="error">
            <CloseIcon fontSize="large" />
          </IconButton>
        </Stack>

        <Grid container spacing={2}>
          {[
            { label: 'Summary', value: 'summary' },
            { label: 'Summary1', value: 'summary1' },
            { label: 'Summary2', value: 'summary2' },
            { label: 'Detail', value: 'detail' },
            { label: 'Detail1', value: 'detail1' },
            { label: 'Group', value: 'group' }
          ].map((item) => (
            <Grid item xs={12} sm={6} key={item.value}>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1.5,
                  padding: 3,
                  border: '1px solid #ddd',
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
                  },
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setValue('type', item.value, { shouldValidate: true });
                }}
              >
                <RadioGroup
                  name="type"
                  value={typeWatch}
                  onChange={(e) => {
                    setValue('type', e.target.value, { shouldValidate: true });
                  }}
                >
                  <FormControlLabel
                    value={item.value}
                    control={<Radio color="primary" />}
                    label={
                      <Typography variant="body1" sx={{ color: '#444' }}>
                        {item.label}
                      </Typography>
                    }
                    sx={{
                      marginX: 2,
                      '& .MuiTypography-root': {
                        fontWeight: 'medium',
                        color: '#444'
                      }
                    }}
                  />
                </RadioGroup>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Alternative: Manual print button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleManualPrint}
            disabled={!typeWatch}
            sx={{ minWidth: '120px' }}
          >
            Print Report
          </Button>
        </Box>
      </Stack>
    </FormProvider>
  );
};